import React from 'react';
import { Controller, useWatch } from 'react-hook-form';
import type {
  Control,
  UseFormRegister,
  FieldErrors,
  UseFormSetValue,
} from 'react-hook-form';
import { FormSelect, VirtualizedFormSelect, Alert } from '@roger/ui-components';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import type {
  KinnserClinicDataResponse,
  KinnserCreatePatientResponse,
} from '../../services/api/types.gen';
import { DateField } from '../../components/FormFields';
import type { SchedulingStepFormData } from '../../schemas/schedulingStepFormSchema';

interface KinnserSchedulingStepFormContentProps {
  // Form controls
  control: Control<SchedulingStepFormData>;
  register: UseFormRegister<SchedulingStepFormData>;
  errors: FieldErrors<SchedulingStepFormData>;
  setValue: UseFormSetValue<SchedulingStepFormData>;
  handleFieldBlur: (fieldEvent: React.FocusEvent<HTMLElement>) => void;

  // Save status
  saveToServer: (data: Partial<SchedulingStepFormData>) => void;

  // Clinics
  clinicData: KinnserClinicDataResponse | null;
  loadingClinicData: boolean;

  // Kinnser creation
  createResult: KinnserCreatePatientResponse | null;

  // Episode creation flag for conditional validation
  kinnserCreateEpisode?: boolean;
}

export const KinnserSchedulingStepFormContent: React.FC<
  KinnserSchedulingStepFormContentProps
> = ({
  control,
  register,
  errors,
  setValue,
  handleFieldBlur,
  saveToServer,
  clinicData,
  loadingClinicData,
  createResult,
}) => {
  // Watch episode start date to check if it's empty when SOC date changes
  const episodeStartDate = useWatch({
    control,
    name: 'kinnser_episode_start_date',
  });

  return (
    <div className='flex flex-col'>
      {/* Validation errors */}
      {Object.keys(errors).length > 0 && (
        <Alert className='mb-3 py-2 px-3 bg-red-50 border-red-100 text-red-900 text-xs'>
          <div className='flex items-start gap-2'>
            <AlertCircle className='h-4 w-4 mt-0.5 flex-shrink-0 text-red-600' />
            <div className='flex-1'>
              <p className='font-medium mb-1'>
                Missing fields required to schedule:
              </p>
              <ul
                className='flex flex-wrap gap-1'
                role='list'
                aria-label='Validation errors'
              >
                {Object.entries(errors)
                  .map(([field, error]) => {
                    const message =
                      (error as { message?: string })?.message || '';
                    const cleanMessage = message.replace(/ is required$/, '');

                    return { field, cleanMessage };
                  })
                  .filter(({ cleanMessage }) => cleanMessage !== '')
                  .map(({ field, cleanMessage }, i, arr) => (
                    <li key={field} role='listitem' className='text-xs'>
                      {cleanMessage}
                      {i < arr.length - 1 ? ',' : ''}
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </Alert>
      )}
      {/* Validation errors or success message */}
      {createResult && (
        <div className='mb-3'>
          {createResult.success ? (
            <div className='p-3 bg-green-50 border border-green-200 rounded-md'>
              <div className='flex items-center gap-2 text-green-800'>
                <CheckCircle className='h-4 w-4' />
                <span className='font-medium'>
                  Patient created successfully in Kinnser
                </span>
              </div>
              {createResult.kinnser_patient_id && (
                <div className='text-sm text-green-700 mt-1'>
                  Kinnser Patient ID: {createResult.kinnser_patient_id}
                </div>
              )}
            </div>
          ) : (
            <div className='p-3 bg-red-50 border border-red-200 rounded-md'>
              <div className='flex items-center gap-2 text-red-800 mb-2'>
                <XCircle className='h-4 w-4' />
                <span className='font-medium'>Validation Errors</span>
              </div>
              <ul className='list-disc list-inside text-sm text-red-700 space-y-1'>
                {createResult.errors?.map((error, index) => (
                  <li key={index}>{error.message}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <form className='space-y-5' onSubmit={e => e.preventDefault()}>
        {/* Schedule Start of Care */}
        <div className='grid grid-cols-4 gap-3'>
          {/* Row 1: Clinical Care Manager & Case Manager */}
          <VirtualizedFormSelect
            label='Clinician'
            placeholder='Select clinician...'
            options={clinicData?.clinicians || []}
            required={true}
            disabled={loadingClinicData}
            error={errors.kinnser_clinician_id?.message}
            {...register('kinnser_clinician_id', {
              onBlur: handleFieldBlur,
            })}
          />

          <VirtualizedFormSelect
            label='Clinical Care Manager'
            placeholder='Select clinical care manager...'
            options={clinicData?.clinical_care_managers || []}
            required={true}
            disabled={loadingClinicData}
            error={errors.kinnser_clinical_care_manager_id?.message}
            {...register('kinnser_clinical_care_manager_id', {
              onBlur: handleFieldBlur,
            })}
          />

          <VirtualizedFormSelect
            label='Case Manager'
            placeholder='Select case manager...'
            options={clinicData?.case_managers || []}
            required={true}
            disabled={loadingClinicData}
            error={errors.kinnser_case_manager_id?.message}
            {...register('kinnser_case_manager_id', {
              onBlur: handleFieldBlur,
            })}
          />

          <VirtualizedFormSelect
            label='OASIS Approver'
            placeholder='Select OASIS approver...'
            options={clinicData?.oasis_approvers || []}
            required={true}
            disabled={loadingClinicData}
            error={errors.kinnser_oasis_approver_id?.message}
            {...register('kinnser_oasis_approver_id', {
              onBlur: handleFieldBlur,
            })}
          />

          {/* Row 2: PT, OT, ST Approvers */}
          <VirtualizedFormSelect
            label='PT Approver'
            placeholder='Select PT approver...'
            options={clinicData?.pt_approvers || []}
            required={true}
            disabled={loadingClinicData}
            error={errors.kinnser_pt_approver_id?.message}
            {...register('kinnser_pt_approver_id', {
              onBlur: handleFieldBlur,
            })}
          />

          <VirtualizedFormSelect
            label='OT Approver'
            placeholder='Select OT approver...'
            options={clinicData?.ot_approvers || []}
            required={true}
            disabled={loadingClinicData}
            error={errors.kinnser_ot_approver_id?.message}
            {...register('kinnser_ot_approver_id', {
              onBlur: handleFieldBlur,
            })}
          />

          <VirtualizedFormSelect
            label='ST Approver'
            placeholder='Select ST approver...'
            options={clinicData?.st_approvers || []}
            required={true}
            disabled={loadingClinicData}
            error={errors.kinnser_st_approver_id?.message}
            {...register('kinnser_st_approver_id', {
              onBlur: handleFieldBlur,
            })}
          />

          {clinicData?.primary_schedulers &&
            clinicData.primary_schedulers.length > 0 && (
              <VirtualizedFormSelect
                label='Primary Scheduler'
                placeholder='Select primary scheduler...'
                options={clinicData.primary_schedulers}
                required={true}
                disabled={loadingClinicData}
                error={errors.kinnser_primary_scheduler_id?.message}
                {...register('kinnser_primary_scheduler_id', {
                  onBlur: handleFieldBlur,
                })}
              />
            )}

          {clinicData?.secondary_schedulers &&
            clinicData.secondary_schedulers.length > 0 && (
              <VirtualizedFormSelect
                label='Secondary Scheduler'
                placeholder='Select secondary scheduler...'
                options={clinicData.secondary_schedulers}
                required={true}
                disabled={loadingClinicData}
                error={errors.kinnser_secondary_scheduler_id?.message}
                {...register('kinnser_secondary_scheduler_id', {
                  onBlur: handleFieldBlur,
                })}
              />
            )}

          {/* Row 3: SOC Type & Dates */}
          <FormSelect
            label='Type of SOC'
            placeholder='Select SOC type...'
            options={[
              { value: 'SN', label: 'Nursing' },
              { value: 'PT', label: 'Therapy' },
            ]}
            required={true}
            disabled={loadingClinicData}
            error={errors.kinnser_type_of_soc?.message}
            {...register('kinnser_type_of_soc', {
              onBlur: handleFieldBlur,
            })}
          />

          <Controller
            name='kinnser_soc_date'
            control={control}
            render={({ field: { value, onChange, onBlur } }) => (
              <DateField
                label='Start of Care Date'
                value={value}
                htmlFor='socDate'
                required={true}
                disabled={false}
                onChange={val => {
                  onChange(val);
                  const toSave: Partial<SchedulingStepFormData> = {
                    kinnser_soc_date: val,
                  };
                  // Copy to episode start date if it's empty (matches Kinnser behavior)
                  if (!episodeStartDate && val) {
                    setValue('kinnser_episode_start_date', val);
                    toSave.kinnser_episode_start_date = val;
                  }
                  saveToServer(toSave);
                }}
                onBlur={onBlur}
                yearRange={{
                  fromYear: new Date().getFullYear() - 1,
                  toYear: new Date().getFullYear() + 1,
                }}
              />
            )}
          />

          <Controller
            name='kinnser_episode_start_date'
            control={control}
            render={({ field: { value, onChange, onBlur } }) => (
              <DateField
                label='Episode Start Date'
                value={value}
                htmlFor='episodeStartDate'
                required={true}
                disabled={false}
                onChange={val => {
                  onChange(val);
                  saveToServer({
                    kinnser_episode_start_date: val,
                  });
                }}
                onBlur={onBlur}
                yearRange={{
                  fromYear: new Date().getFullYear() - 1,
                  toYear: new Date().getFullYear() + 1,
                }}
              />
            )}
          />
        </div>
      </form>
    </div>
  );
};
